/*
 * Signal — the notify-me dialog.
 * ---------------------------------------------------------------------------
 * One <dialog> per page (snippets/signal-notify-dialog.liquid), opened by any
 * control carrying snippets/signal-notify-trigger.liquid's attributes. The
 * trigger describes the product; this fills the dialog in from it and writes the
 * email body. See that snippet for why the form is shared rather than repeated
 * per card.
 *
 * PROGRESSIVE ENHANCEMENT. Every trigger is already a working link to the
 * product page, where the same capture renders inline in the buy area. Nothing
 * here calls preventDefault until it has found a dialog to open, so scripting
 * off, a failed asset load, or a page that renders triggers without the dialog
 * all fall back to the navigation the merchant would have got anyway.
 *
 * THE BODY IS A PARSING CONTRACT. The lines written below have to match the ones
 * snippets/signal-notify.liquid writes in Liquid, key for key and order for
 * order — the mailbox is drained by script, and the two paths produce mail that
 * has to read the same. CLAUDE.md, "Notify me", is the reference.
 *
 * THE THUMBNAIL IS THE BITMAP THE PAGE ALREADY PAINTED. Every trigger carries a
 * fixed-width image URL, but the surface that raised the dialog — a card, the
 * spotlight, the product gallery — has the same file on screen at whatever
 * width its srcset chose, and to the browser a different `?width=` is a
 * different resource. So the dialog was fetching the product's own artwork a
 * second time, and its thumbnail landed a beat after the rest of the panel.
 * paintedImageFor() reuses the exact URL the nearest painted <img> resolved to,
 * walking outward until it finds one at least as wide as the thumbnail box —
 * the sticky bar's own 120px image would be soft on a 3x phone, where the
 * gallery's copy of the same file is a few elements further out. The trigger's
 * URL is only the fallback.
 *
 * Plain custom-element-free script, matching signal-collection.js: the work is
 * one delegated listener and a handful of DOM writes, and none of it needs a
 * component lifecycle.
 */

const DIALOG_SELECTOR = '[data-signal-notify-dialog]';
const TRIGGER_SELECTOR = '[data-signal-notify]';

// Survives the round trip through /contact. Shopify's redirect brings back a
// success flag but not which product it was for, and re-rendering the dialog's
// heading as "Sold out" over someone else's confirmation reads as the wrong
// product being watched.
const STORAGE_KEY = 'signal-notify-context';

// Same round trip, same reason: a full-page reload back to a collection drops
// every page infinite scroll had appended, landing the shopper at the top.
// Scoped to this flow only — the buy-area form on a product page has no
// scroll position worth restoring.
const SCROLL_STORAGE_KEY = 'signal-notify-scroll';

const LOCK_CLASS = 'signal-notify-open';

/**
 * Everything the dialog shows and sends, lifted off the trigger that opened it.
 *
 * @typedef {object} NotifyContext
 * @property {'release' | 'restock'} state
 * @property {string} title
 * @property {string} sku
 * @property {string} variantId
 * @property {string} url
 * @property {string} image
 * @property {string} release - Machine date, for the email body.
 * @property {string} releaseLabel - The same date, worded for the heading.
 */

/**
 * How many device pixels wide a painted copy has to be to look sharp in the
 * dialog's thumbnail box. Read off the box rather than repeated here — the size
 * lives in signal-notify.css — and readable while the box is still hidden,
 * because a specified width computes the same with no layout box. Zero when it
 * cannot be read, which makes any painted copy good enough.
 *
 * @param {HTMLDialogElement} dialog
 * @returns {number}
 */
function thumbnailNeed(dialog) {
  const media = dialog.querySelector('[data-signal-notify-media]');
  if (!(media instanceof HTMLElement)) return 0;

  const css = parseFloat(getComputedStyle(media).width);

  return css > 0 ? css * (window.devicePixelRatio || 1) : 0;
}

/**
 * The URL to draw the product from: what a nearby <img> is already showing for
 * the same file, or the trigger's own fixed-width URL when nothing on the page
 * has it painted. See the header.
 *
 * The file is matched by CDN path, so only the same artwork ever qualifies —
 * never a neighbouring card's — and only once it has actually loaded, so the
 * dialog never inherits a lazy image that has not started downloading yet.
 * Reusing currentSrc rather than copying the srcset matters: given a srcset the
 * browser would re-pick for the dialog's box and choose a smaller candidate it
 * has not got, which is the round trip this exists to avoid.
 *
 * The first painted copy wide enough for the box wins; failing that, the widest
 * painted copy there is — a soft thumbnail now still beats a sharp one a
 * second later, which is the complaint this fixes. Shopify's CDN names the
 * raster's width in the URL's query, and an unsized URL is the original.
 *
 * @param {Element} trigger
 * @param {string} fallback - The trigger's own URL.
 * @param {number} need - Device pixels the box wants, from thumbnailNeed().
 * @returns {string}
 */
function paintedImageFor(trigger, fallback, need) {
  if (!fallback) return '';

  let path;
  try {
    path = new URL(fallback, window.location.href).pathname;
  } catch (error) {
    return fallback;
  }

  /** @type {{ url: string, width: number } | null} */
  let widest = null;

  // Outward from the trigger, so the card that raised this wins over another
  // surface showing the same product elsewhere on the page.
  for (let scope = trigger.parentElement; scope; scope = scope.parentElement) {
    for (const image of scope.querySelectorAll('img')) {
      if (!image.complete || image.naturalWidth === 0 || !image.currentSrc) continue;

      let url;
      try {
        url = new URL(image.currentSrc, window.location.href);
      } catch (error) {
        continue;
      }
      if (url.pathname !== path) continue;

      const width = Number(url.searchParams.get('width')) || Infinity;
      if (width >= need) return image.currentSrc;
      if (!widest || width > widest.width) widest = { url: image.currentSrc, width };
    }
  }

  return widest ? widest.url : fallback;
}

/** @param {Element} trigger @param {HTMLDialogElement} dialog @returns {NotifyContext} */
function readContext(trigger, dialog) {
  const data = /** @type {HTMLElement} */ (trigger).dataset;

  return {
    state: data.signalNotify === 'release' ? 'release' : 'restock',
    title: data.signalNotifyTitle ?? '',
    sku: data.signalNotifySku ?? '',
    variantId: data.signalNotifyVariant ?? '',
    url: data.signalNotifyUrl ?? '',
    image: paintedImageFor(trigger, data.signalNotifyImage ?? '', thumbnailNeed(dialog)),
    release: data.signalNotifyRelease ?? '',
    releaseLabel: data.signalNotifyReleaseLabel ?? '',
  };
}

/**
 * The email body. Stable `key: value` lines, one per field — see the header.
 *
 * @param {NotifyContext} context
 * @returns {string}
 */
function buildBody(context) {
  const lines = [
    `notify-request: ${context.state}`,
    `product: ${context.title}`,
    `sku: ${context.sku}`,
    `variant-id: ${context.variantId}`,
    `url: ${context.url}`,
  ];

  if (context.state === 'release' && context.release) {
    lines.push(`release-date: ${context.release}`);
  }

  return lines.join('\n');
}

/** @param {HTMLDialogElement} dialog @param {NotifyContext} context */
function paint(dialog, context) {
  const { dataset } = dialog;
  const isRelease = context.state === 'release' && Boolean(context.releaseLabel);

  const media = dialog.querySelector('[data-signal-notify-media]');
  const image = /** @type {HTMLImageElement | null} */ (
    dialog.querySelector('[data-signal-notify-image]')
  );

  if (media instanceof HTMLElement && image) {
    if (context.image) {
      image.src = context.image;
      media.hidden = false;
    } else {
      image.removeAttribute('src');
      media.hidden = true;
    }
  }

  const product = dialog.querySelector('[data-signal-notify-product]');
  if (product) product.textContent = context.title;

  // Same line the card that raised this is showing: a dot, three words, and the
  // colour carrying which of the two states it is.
  const status = dialog.querySelector('[data-signal-notify-status]');
  if (status instanceof HTMLElement) {
    status.textContent = isRelease
      ? (dataset.signalLabelReleaseStatus ?? '').replace('[date]', context.releaseLabel)
      : dataset.signalLabelRestockStatus ?? '';
    status.dataset.state = isRelease ? 'release' : 'restock';
  }

  // Absent on the success re-render, where the form has already been replaced by
  // its confirmation.
  const submit = dialog.querySelector('[data-signal-notify-submit]');
  if (submit) {
    submit.textContent = isRelease
      ? dataset.signalLabelReleaseButton ?? ''
      : dataset.signalLabelRestockButton ?? '';
  }

  const body = /** @type {HTMLInputElement | null} */ (
    dialog.querySelector('[data-signal-notify-body]')
  );
  if (body) body.value = buildBody(context);
}

function lockScroll() {
  // Measured before the scrollbar goes, and paid back as padding, so locking the
  // page does not shunt the sticky header and the whole layout sideways.
  const gap = window.innerWidth - document.documentElement.clientWidth;

  document.documentElement.style.setProperty('--signal-scrollbar-gap', `${gap}px`);
  document.documentElement.classList.add(LOCK_CLASS);
}

function unlockScroll() {
  document.documentElement.classList.remove(LOCK_CLASS);
  document.documentElement.style.removeProperty('--signal-scrollbar-gap');
}

/** @param {NotifyContext} context */
function remember(context) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch (error) {
    // Storage is unavailable in a partitioned iframe or with cookies blocked.
    // The submission still works; only the heading after it is lost.
  }
}

/** @returns {NotifyContext | null} */
function recall() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);

    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
}

function rememberScroll() {
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
  } catch (error) {
    // Storage is unavailable; the shopper just lands at the top on return,
    // same as before this fix.
  }
}

/** @returns {number | null} */
function recallScroll() {
  try {
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);

    return stored === null ? null : Number(stored);
  } catch (error) {
    return null;
  }
}

/** @param {HTMLDialogElement} dialog */
function open(dialog) {
  if (dialog.open) return;

  lockScroll();
  dialog.showModal();
}

/** @param {HTMLDialogElement} dialog */
function close(dialog) {
  if (!dialog.open) return;

  dialog.close();
}

/** @returns {HTMLDialogElement | null} */
function findDialog() {
  const dialog = document.querySelector(DIALOG_SELECTOR);

  return dialog instanceof HTMLDialogElement ? dialog : null;
}

// Delegated, because <results-list> replaces the collection grid on every facet
// and sort change and infinite scroll appends more of it. Nothing here has to
// know that happened.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('[data-signal-notify-close]')) {
    const dialog = findDialog();
    if (dialog) close(dialog);

    return;
  }

  const trigger = target.closest(TRIGGER_SELECTOR);
  if (!trigger) return;

  const dialog = findDialog();
  // No dialog on this page: leave the click alone and let the link do its job.
  if (!dialog) return;

  event.preventDefault();

  const context = readContext(trigger, dialog);
  remember(context);
  paint(dialog, context);
  open(dialog);
});

const dialog = findDialog();

if (dialog) {
  // A click on the backdrop lands on the dialog itself — the panel inside it is
  // what covers the rest — so anything outside the panel's box is an outside
  // click. Escape is native and fires 'close' through 'cancel'.
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;

    const box = dialog.getBoundingClientRect();
    const outside =
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom;

    if (outside) close(dialog);
  });

  dialog.addEventListener('close', unlockScroll);

  // The form posts natively — see the header — so this is the only chance to
  // stash where the shopper was before the full-page reload takes it away.
  const form = dialog.querySelector('form');
  if (form) form.addEventListener('submit', rememberScroll);

  // iOS doesn't shrink the layout viewport for the keyboard, so the fixed,
  // top-anchored dialog can still end up with its submit button behind it.
  // Nudging the focused field into view once the keyboard has finished
  // animating pulls the whole panel up with it.
  dialog.addEventListener('focusin', (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || field.type !== 'email') return;

    window.setTimeout(() => {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 350);
  });

  // Came back from /contact. The form's own state is server-rendered; all that
  // is missing is which product it was for, which the stash still holds.
  const outcome = dialog.querySelector('[data-signal-notify-outcome]');

  if (outcome) {
    const context = recall();
    if (context) paint(dialog, context);

    // Restores the scroll position stashed on submit — before open() locks
    // the page with overflow: hidden, past which a scroll here is a no-op —
    // so reopening the dialog doesn't also strand the shopper at the top.
    const scrollY = recallScroll();
    if (scrollY !== null) window.scrollTo(0, scrollY);

    open(dialog);

    if (outcome instanceof HTMLElement) outcome.focus();
  }
}
